import GetLocalStorageUserId from "../components/sharedComponents/GetLocalStorageuserId";
import {
  PARCEL_BOOKING_REFRESH_EVENT,
} from "../types/parcelBookingSignalR.types";

const SELF_SUPPRESS_KEY = "parcelBookingSelfSuppress";
const SELF_SUPPRESS_MS = 60_000;

/**
 * @param {import('../types/parcelBookingSignalR.types').CreateParcelBookingOutputView[]} results
 */
export function markSelfParcelBookingCreated(results) {
  const items = Array.isArray(results) ? results : [];
  if (items.length === 0) return;

  const userId = GetLocalStorageUserId();
  sessionStorage.setItem(
    SELF_SUPPRESS_KEY,
    JSON.stringify({
      userId: userId ? String(userId) : "",
      parcelIds: items
        .map((r) => r?.parcelId)
        .filter((id) => id != null),
      at: Date.now(),
    }),
  );
}

/**
 * @param {import('../types/parcelBookingSignalR.types').ParcelBookingCreatedPayload} payload
 */
export function shouldSuppressParcelBookingNotification(payload) {
  try {
    const raw = sessionStorage.getItem(SELF_SUPPRESS_KEY);
    if (!raw) return false;

    const { userId, parcelIds, at } = JSON.parse(raw);
    if (!at || Date.now() - at > SELF_SUPPRESS_MS) return false;

    const currentUserId = GetLocalStorageUserId();
    if (!currentUserId || !userId) return false;
    if (String(currentUserId) !== String(userId)) return false;

    const items = Array.isArray(payload) ? payload : [payload];
    if (items.length === 0) return false;

    const suppressIds = new Set((parcelIds || []).map((id) => Number(id)));
    return items.every(
      (item) =>
        String(item?.userId) === String(userId) &&
        suppressIds.has(Number(item?.parcelId)),
    );
  } catch {
    return false;
  }
}

/**
 * @param {import('../types/parcelBookingSignalR.types').ParcelBookingCreatedPayload} payload
 */
export function normalizeParcelBookingPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") return [payload];
  return [];
}

/**
 * @param {import('../types/parcelBookingSignalR.types').ParcelBookingCreatedPayload} payload
 */
export function formatParcelBookingToastMessage(payload) {
  const items = normalizeParcelBookingPayload(payload);
  if (items.length === 0) {
    return "A new parcel booking was created.";
  }

  if (items.length === 1) {
    const item = items[0];
    const parts = ["New parcel booking created"];
    if (item.recordSerialNo) parts.push(`Ref: ${item.recordSerialNo}`);
    if (item.barcode) parts.push(`Barcode: ${item.barcode}`);
    if (item.parcelId) parts.push(`#${item.parcelId}`);
    return parts.join(" · ");
  }

  const labels = items
    .slice(0, 3)
    .map((item) => item.recordSerialNo || item.barcode || `#${item.parcelId}`)
    .filter(Boolean);

  const suffix =
    items.length > 3 ? ` (+${items.length - 3} more)` : "";
  return `New parcel bookings: ${labels.join(", ")}${suffix}`;
}

/**
 * Customer event: ParcelStatusUpdate
 * @param {unknown} payload
 */
export function formatParcelStatusUpdateToastMessage(payload) {
  const items = normalizeParcelBookingPayload(payload);

  if (items.length === 0) {
    return "Parcel status updated.";
  }

  if (items.length === 1) {
    const item = items[0];
    const parts = ["Parcel status updated"];
    if (item.recordSerialNo) parts.push(`Ref: ${item.recordSerialNo}`);
    if (item.barcode) parts.push(`Barcode: ${item.barcode}`);
    if (item.parcelId) parts.push(`#${item.parcelId}`);
    return parts.join(" · ");
  }

  const labels = items
    .slice(0, 3)
    .map((item) => item.recordSerialNo || item.barcode || `#${item.parcelId}`)
    .filter(Boolean);

  const suffix =
    items.length > 3 ? ` (+${items.length - 3} more)` : "";
  return `Parcel status updated: ${labels.join(", ")}${suffix}`;
}

/**
 * @param {import('../types/parcelBookingSignalR.types').ParcelBookingCreatedPayload} payload
 */
export function dispatchParcelBookingRefresh(payload) {
  window.dispatchEvent(
    new CustomEvent(PARCEL_BOOKING_REFRESH_EVENT, { detail: payload }),
  );
}
